import { io } from 'socket.io-client';
import cookie from 'js-cookie';
import { subscribeToAuthChange } from './util/authChange';

// "undefined" means the URL will be computed from the `window.location` object
const URL = process.env.NEXT_PUBLIC_DB_URL;

// Get or create a browser instance ID stored in localStorage.
// All tabs in the same browser share the same ID, but different
// browsers/devices will have different IDs. This allows multi-tab
// usage while still preventing multi-device credential sharing.
export function getBrowserInstanceId() {
  if (typeof window === 'undefined') return null;
  try {
    let id = localStorage.getItem('browserInstanceId');
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem('browserInstanceId', id);
    }
    return id;
  } catch (e) {
    console.warn('Unable to access localStorage for browserInstanceId:', e);
    return null;
  }
}

// The users-permissions JWT the server verifies before it lets a socket into
// the staff company room (and before it trusts the socket's user id at all).
// Read fresh on every use: the cookie changes on sign-in / sign-out while the
// socket can stay connected. Never log the value.
export function getSocketAuthToken() {
  try {
    return cookie.get('jwt') || null;
  } catch (e) {
    return null;
  }
}

// The token lives in the socket.io HANDSHAKE only, never in an event payload:
// the previous server build logged every `register` payload verbatim, so a
// payload token would put live bearer JWTs in the server logs whenever this
// client is deployed ahead of the server or the server is rolled back.
//
// The handshake is frozen for the life of a connection, so the token it was
// built with is remembered here, and any identity-bearing emit first
// reconnects if the cookie has changed since (sign-in, sign-out, a different
// user). Emits made while reconnecting are buffered by socket.io-client and
// sent on the new connection, after the fresh handshake.
let handshakeToken; // undefined until the auth callback has run

export function socketHandshakeAuth(cb) {
  handshakeToken = getSocketAuthToken();
  cb({ token: handshakeToken });
}

export function ensureFreshSocketAuth() {
  if (!socket || handshakeToken === undefined) return false;
  if (getSocketAuthToken() === handshakeToken) return false;
  handshakeToken = undefined;
  try {
    socket.disconnect();
    socket.connect();
  } catch (error) {
    console.error('Error refreshing socket session:', error);
  }
  return true;
}

// The last sanitized `register` payload, whichever path emitted it
// (connectAuthenticatedSocket, registerUser, useSocket). A LOOKUP_FAILED retry
// re-sends exactly this rather than rebuilding ids from cookies/localStorage,
// which may be absent or differ from what was originally registered.
let lastRegistration = null;

// Whether this tab's page asked for a registered (staff) socket — the last
// connectWithRegistration() call. Lets a cross-tab sign-in restore it.
let registrationEnabled = false;

const userIdOf = (raw) => {
  try {
    return raw ? JSON.parse(raw)?.id ?? null : null;
  } catch (e) {
    return null;
  }
};

// Another tab changed the jwt cookie (see util/authChange). Sign-out drops this
// tab's socket out of the staff room now; a new token reconnects so the
// handshake carries it, and the connect handler re-registers. Either way the
// remembered registration belonged to the old identity, so a LOOKUP_FAILED
// retry must not re-send it.
export function applyAuthChange() {
  if (!socket) return;
  const live = socket.connected || socket.active;
  if (!getSocketAuthToken()) {
    handshakeToken = undefined;
    lastRegistration = null;
    if (live) {
      try {
        socket.disconnect(); // client disconnect: socket.io does not reconnect
      } catch (error) {
        console.error('Error disconnecting socket after sign-out:', error);
      }
    }
    return;
  }
  if (live) {
    if (ensureFreshSocketAuth()) lastRegistration = null;
  } else if (registrationEnabled) {
    lastRegistration = null;
    socket.connectWithRegistration(true);
  }
}

// The signing-in tab writes localStorage `user` after the cookie, so the
// reconnect above can register with the previous user's id (and be refused).
// Re-register once the id actually changes.
function handleStoredUserChange(event) {
  if (event.key !== 'user') return;
  if (!registrationEnabled || !socket?.connected || !getSocketAuthToken()) return;
  const nextId = userIdOf(event.newValue);
  if (nextId == null || nextId === userIdOf(event.oldValue)) return;
  connectAuthenticatedSocket();
}

// For every `register` payload: makes sure the handshake carries the current
// token, and guarantees the payload itself does not.
export function withFreshHandshake(payload) {
  ensureFreshSocketAuth();
  const rest = { ...(payload || {}) };
  delete rest.token;
  lastRegistration = JSON.parse(JSON.stringify(rest));
  return rest;
}

export function retryLastRegistration() {
  if (!socket || !socket.connected || !lastRegistration) return false;
  socket.emit('register', withFreshHandshake(lastRegistration));
  return true;
}

// Declare socket variable that can be reassigned
let socket = null;

// Cache for mock socket to avoid global caching issues
let mockSocket = null;

// Helper function to check if we're in a Stripe iframe
const isInStripeIframe = () => {
  if (typeof window === 'undefined') return false;

  try {
    if (window.self !== window.top) {
      const currentUrl = window.location.href;
      return (
        currentUrl.includes('stripe_internal_running_in_iframe=true') ||
        currentUrl.includes('data_layer_') ||
        currentUrl.includes('ui_layer_')
      );
    }
  } catch (error) {
    console.warn('Error checking iframe context:', error);
  }
  return false;
};

// Create a function to initialize the socket with error handling
const initSocket = (url, options = {}) => {
  // Check if we're running inside a Stripe iframe to avoid session conflicts
  if (isInStripeIframe()) {
    console.log(
      'Detected Stripe iframe environment, skipping socket connection'
    );
    // Return a cached mock socket object to prevent errors
    if (!mockSocket) {
      mockSocket = {
        connected: false,
        connect: () => {},
        disconnect: () => {},
        emit: () => {},
        on: () => {},
        off: () => {},
      };
    }
    return mockSocket;
  }

  if (!socket) {
    try {
      // Default socket configuration
      const defaultOptions = {
        cors: {
          origin: process.env.NEXT_PUBLIC_DB_URL,
        },
        autoConnect: false, // Don't connect automatically
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        timeout: 10000, // Timeout after 10 seconds
        transports: ['websocket', 'polling'], // Try WebSocket first, then fall back to polling
        // Handshake auth — called on every (re)connect, so it follows the
        // current cookie. The only place the token is sent (see
        // withFreshHandshake).
        auth: socketHandshakeAuth,
      };

      // Merge default options with passed options
      socket = io(url, {
        ...defaultOptions,
        ...options,
      });

      // Store the connect handler to avoid duplicate event listeners
      const handleConnect = (shouldRegister) => () => {
        console.log('🔌 Socket connected with ID:', socket.id, 'shouldRegister:', shouldRegister);

        // Auto-register if enabled and we have authentication
        if (shouldRegister) {
          try {
            const jwt = cookie.get('jwt');
            const companyData = cookie.get('company');

            console.log('🔐 Auth check - JWT exists:', !!jwt, 'Company data exists:', !!companyData);

            if (jwt && companyData) {
              const company = JSON.parse(companyData);
              console.log('🏢 Company data parsed:', company);
              const companyId = parseInt(company.id, 10);

              if (isNaN(companyId)) {
                console.error('❌ Company ID is not a valid number:', company.id);
                return;
              }

              // Get user data from localStorage
              let userData;
              try {
                userData = localStorage.getItem('user');
              } catch (err) {
                console.warn('⚠️ Unable to access localStorage:', err);
                userData = null;
              }

              console.log('👤 User data from localStorage exists:', !!userData);

              if (userData) {
                try {
                  const user = JSON.parse(userData);
                  console.log('👤 User data parsed:', user);
                  if (user && user.id) {
                    const userId = parseInt(user.id, 10);
                    if (isNaN(userId)) {
                      console.error('❌ User ID is not a valid number:', user.id);
                      return;
                    }

                    console.log(
                      '📤 Auto-registering user on connect:',
                      userId,
                      'for company:',
                      companyId
                    );

                    // Add a small delay to ensure socket is fully established
                    setTimeout(() => {
                      if (socket.connected) {
                        console.log('✅ Emitting register event with userId:', userId, 'companyId:', companyId);
                        socket.emit(
                          'register',
                          withFreshHandshake({
                            id: userId,
                            company: {
                              id: companyId,
                            },
                            browserInstanceId: getBrowserInstanceId(),
                          }),
                        );

                        // Add confirmation listener
                        socket.once('registration-confirmed', (data) => {
                          console.log('✅ Registration confirmed by server:', data);
                        });
                      } else {
                        console.error('❌ Socket disconnected before registration could complete');
                      }
                    }, 500);
                  } else {
                    console.error(
                      '❌ No valid user ID in localStorage data:',
                      userData
                    );
                  }
                } catch (e) {
                  console.error(
                    '❌ Error parsing user data from localStorage:',
                    e
                  );
                }
              } else {
                console.error('❌ No user data found in localStorage');
              }
            } else {
              console.log('⚠️ Missing JWT or company cookie, not registering');
            }
          } catch (e) {
            console.error('❌ Error parsing company data:', e);
          }
        } else {
          console.log('⚠️ Auto-registration disabled, not registering on connect');
        }
      };

      // Store the handler factory for use when connecting
      socket.connectWithRegistration = (shouldRegister) => {
        registrationEnabled = !!shouldRegister;
        // Remove any existing connect handlers to prevent duplicates
        socket.off('connect');
        socket.on('connect', handleConnect(shouldRegister));
        socket.connect();
      };

      // Follow sign-in / sign-out in other tabs (module singleton: once).
      if (typeof window !== 'undefined') {
        subscribeToAuthChange(applyAuthChange);
        window.addEventListener('storage', handleStoredUserChange);
      }

      // Add specific handling for session-conflict events
      socket.on('session-conflict', (data) => {
        console.log('Socket received session-conflict event directly:', data);
      });

      // The server refused the registration (no/expired token, or the token
      // is a different user). The socket gets the public storefront feed only.
      // LOOKUP_FAILED is different: the server could not read the session
      // (DB busy) and changed nothing, so try again with a bounded backoff.
      let lookupRetries = 0;
      socket.on('registration-error', (data) => {
        console.warn('Socket registration refused:', data?.code || data?.error);
        if (data?.code === 'LOOKUP_FAILED' && lookupRetries < 5) {
          const delay = Math.min(30000, 2000 * 2 ** lookupRetries);
          lookupRetries += 1;
          setTimeout(retryLastRegistration, delay);
        }
      });
      socket.on('registration-confirmed', () => {
        lookupRetries = 0;
      });

      socket.on('disconnect', (reason) => {
        console.log('Socket disconnected, reason:', reason);

        // Socket.io's built-in reconnection (reconnection: true) handles
        // automatic reconnects. We only need to manually reconnect for
        // "io server disconnect" which socket.io does NOT auto-reconnect.
        // "io client disconnect" means we intentionally disconnected (e.g.
        // session-conflict handler) so we should NOT reconnect.
        if (reason === 'io server disconnect') {
          // Server forced the disconnect — but NOT due to session conflict
          // (session-conflict handler calls disconnectSocket() which sets
          // reason to 'io client disconnect'). Safe to reconnect.
          setTimeout(() => {
            if (!socket.connected) {
              console.log('Attempting to reconnect after server disconnect...');
              socket.connect();
            }
          }, 2000);
        }
      });

      socket.on('connect_error', (error) => {
        console.error('Socket connection error:', error);
      });

      socket.on('reconnect', (attemptNumber) => {
        console.log('Socket reconnected after', attemptNumber, 'attempts');
      });

      socket.on('reconnect_attempt', (attemptNumber) => {
        console.log('Socket reconnection attempt:', attemptNumber);
      });

      socket.on('reconnect_error', (error) => {
        console.error('Socket reconnection error:', error);
      });

      socket.on('reconnect_failed', () => {
        console.error('Socket reconnection failed after all attempts');
      });
    } catch (error) {
      console.error('Error initializing socket:', error);
      return null;
    }
  }

  return socket;
};

// Initialize the socket
socket = initSocket(URL);

// Export the socket for use in other modules
export { socket };

// Connect to socket with auto-registration for authenticated pages
export const connectAuthenticatedSocket = () => {
  // Check if user is properly authenticated before proceeding
  const jwt = cookie.get('jwt');
  const companyData = cookie.get('company');
  const userData = localStorage.getItem('user');

  if (!jwt || !companyData || !userData) {
    console.log('Not fully authenticated, skipping socket registration');
    // Still connect the socket but don't register
    if (!socket.connected) {
      try {
        console.log(
          'Connecting socket without registration (not authenticated)'
        );
        socket.connectWithRegistration(false);
      } catch (error) {
        console.error('Error connecting socket:', error);
      }
    }
    return;
  }

  if (!socket.connected) {
    try {
      console.log('Connecting socket with auto-registration...');
      socket.connectWithRegistration(true);
    } catch (error) {
      console.error('Error connecting to socket:', error);
    }
  } else {
    console.log('🔄 Socket already connected with ID:', socket.id);
    // If already connected, manually trigger registration
    try {
      const company = JSON.parse(companyData);
      console.log('🏢 Company data for manual registration:', company);
      if (!company || !company.id) {
        console.error('❌ Invalid company data format:', companyData);
        return;
      }

      const companyId = parseInt(company.id, 10);
      if (isNaN(companyId)) {
        console.error('❌ Company ID is not a valid number:', company.id);
        return;
      }

      try {
        const user = JSON.parse(userData);
        console.log('👤 User data for manual registration:', user);
        if (user && user.id) {
          const userId = parseInt(user.id, 10);
          if (isNaN(userId)) {
            console.error('❌ User ID is not a valid number:', user.id);
            return;
          }

          console.log(
            '📤 Manually registering user on already connected socket:',
            userId,
            'for company:',
            companyId
          );
          socket.emit(
            'register',
            withFreshHandshake({
              id: userId,
              company: {
                id: companyId,
              },
              browserInstanceId: getBrowserInstanceId(),
            }),
          );

          // Add debug to check rooms
          setTimeout(() => {
            console.log('🔍 Requesting room info from server...');
            socket.emit('get-rooms');
            socket.once('rooms-info', (data) => {
              console.log('📋 Socket room info:', data);
            });
          }, 1000);
        } else {
          console.error('❌ User object missing ID:', user);
        }
      } catch (e) {
        console.error('❌ Error parsing user data:', e);
      }
    } catch (e) {
      console.error('❌ Error parsing company data:', e);
    }
  }
};

// Connect to socket without auto-registration for public pages
export const connectSocket = () => {
  if (!socket.connected) {
    try {
      console.log('Connecting socket without auto-registration...');
      socket.connectWithRegistration(false);
    } catch (error) {
      console.error('Error connecting to socket:', error);
    }
  } else {
    console.log('Socket already connected with ID:', socket.id);
  }
};

// Disconnect when not needed
export const disconnectSocket = () => {
  if (socket.connected) {
    try {
      console.log('Disconnecting socket with ID:', socket.id);
      socket.disconnect();
    } catch (error) {
      console.error('Error disconnecting socket:', error);
    }
  } else {
    console.log('Socket already disconnected');
  }
};

// Register user with socket
export const registerUser = (userId, companyId) => {
  if (!userId || !companyId) {
    console.error('Cannot register socket: userId or companyId is missing', {
      userId,
      companyId,
    });
    return;
  }

  // Ensure we're working with numbers or valid IDs
  const userIdNumber = parseInt(userId, 10);
  const companyIdNumber = parseInt(companyId, 10);

  if (isNaN(userIdNumber) || isNaN(companyIdNumber)) {
    console.error('Invalid userId or companyId format', { userId, companyId });
    return;
  }

  if (socket.connected) {
    try {
      console.log(
        'Registering user with socket:',
        userIdNumber,
        'for company:',
        companyIdNumber
      );
      socket.emit(
        'register',
        withFreshHandshake({
          id: userIdNumber,
          company: {
            id: companyIdNumber,
          },
          browserInstanceId: getBrowserInstanceId(),
        }),
      );
    } catch (error) {
      console.error('Error registering user with socket:', error);
    }
  } else {
    console.log('Socket not connected, connecting first...');
    socket.connectWithRegistration(true); // Enable auto-registration for this connection
    setTimeout(() => {
      if (socket.connected) {
        console.log('Socket now connected, registering user:', userIdNumber);
        socket.emit(
          'register',
          withFreshHandshake({
            id: userIdNumber,
            company: {
              id: companyIdNumber,
            },
            browserInstanceId: getBrowserInstanceId(),
          }),
        );
      } else {
        console.error('Socket still not connected after connect attempt');
      }
    }, 1000);
  }
};
